import { FC, FormEvent, useEffect, useState } from 'react';
import { Button, CircularProgress, TextField, Typography } from '@mui/material';
import { Box } from '@mui/material';
import BrandDialog from '../../components/BrandDialog';
import FormRow from '../../components/FormRow';
import { ON_PRIMARY_ERROR_SX, ON_PRIMARY_FIELD_SX } from '../../components/fieldStyles';
import { archivosFetch } from '../../utils/fetch';

// "Changer le mot de passe" (no design in the XD, gap 3): a second modal
// over "Mon profil", with the pattern of the XD forms (labels on
// the left, "Valider" orange, "Annuler"). Three fields: Ancien mot de
// passe, Nouveau mot de passe, Confirmez.
//
// Client-side validation (gap 8): the three required and the confirmation
// equal to the new one; the errors inline under each field. The backend
// (`POST /users/change-password`) does not impose any rule on the new
// mot de passe, so none is invented here either. A 401 is the old one
// being wrong, and it is flagged in its field.

type Props = {
  open: boolean;
  userId: string;
  onClose: () => void;
  onChanged: () => void;
};

type Fields = {
  oldPassword: string;
  newPassword: string;
  confirmation: string;
};

type Errors = Partial<Record<keyof Fields, string>>;

const EMPTY: Fields = { oldPassword: '', newPassword: '', confirmation: '' };

const REQUIRED = 'Ce champ est obligatoire.';
const GENERIC_ERROR =
  "L'opération n'a pas pu aboutir. Veuillez réessayer dans quelques instants.";

const validate = (fields: Fields): Errors => {
  const errors: Errors = {};
  if (!fields.oldPassword) errors.oldPassword = REQUIRED;
  if (!fields.newPassword) errors.newPassword = REQUIRED;
  if (!fields.confirmation) {
    errors.confirmation = REQUIRED;
  } else if (fields.newPassword && fields.confirmation !== fields.newPassword) {
    errors.confirmation = 'La confirmation ne correspond pas au nouveau mot de passe.';
  }
  return errors;
};

const ChangePasswordDialog: FC<Props> = ({ open, userId, onClose, onChanged }) => {
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFields(EMPTY);
      setErrors({});
      setFormError(undefined);
    }
  }, [open]);

  const update = (key: keyof Fields, value: string) => {
    setFields((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[key];
      if (key === 'newPassword') delete next.confirmation;
      return next;
    });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;

    const found = validate(fields);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    setSaving(true);
    setFormError(undefined);
    try {
      const response = await archivosFetch(
        `${process.env.REACT_APP_API_URL}/api/users/change-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: userId,
            old_password: fields.oldPassword,
            new_password: fields.newPassword,
          }),
        },
      );

      if (response.ok) {
        onChanged();
      } else if (response.status === 401) {
        setErrors({ oldPassword: "L'ancien mot de passe est incorrect." });
      } else {
        setFormError(GENERIC_ERROR);
      }
    } catch (_) {
      setFormError(GENERIC_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const field = (
    key: keyof Fields,
    id: string,
    name: string,
    autoComplete: string,
  ) => (
    <TextField
      id={id}
      name={name}
      type="password"
      size="small"
      fullWidth
      autoComplete={autoComplete}
      value={fields[key]}
      onChange={(e) => update(key, e.target.value)}
      error={Boolean(errors[key])}
      disabled={saving}
      sx={ON_PRIMARY_FIELD_SX}
    />
  );

  return (
    <BrandDialog
      open={open}
      title="Changer le mot de passe"
      onClose={onClose}
      onSubmit={submit}
      busy={saving}
      maxWidth="sm"
      actions={
        <>
          <Button variant="outlined" color="inherit" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Valider
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {formError && (
          <Typography role="alert" sx={{ ...ON_PRIMARY_ERROR_SX, fontSize: '0.875rem' }}>
            {formError}
          </Typography>
        )}
        <FormRow label="Ancien mot de passe" htmlFor="old-password" error={errors.oldPassword}>
          {field('oldPassword', 'old-password', 'old_password', 'current-password')}
        </FormRow>
        <FormRow label="Nouveau mot de passe" htmlFor="new-password" error={errors.newPassword}>
          {field('newPassword', 'new-password', 'new_password', 'new-password')}
        </FormRow>
        <FormRow label="Confirmez" htmlFor="confirm-password" error={errors.confirmation}>
          {field('confirmation', 'confirm-password', 'confirm_password', 'new-password')}
        </FormRow>
      </Box>
    </BrandDialog>
  );
};

export default ChangePasswordDialog;
